// ────────────────────────────────────────────────────────────────────────
// AddCustomSwatchModal — collects a one-off fabric upload + the silhouette
// the tailor wants rendered in that fabric (replaces the legacy "Fabric
// Swatches" grid + Category/Style dropdowns + Generate button in the
// Custom Fabric tab). Visual language mirrors WeddingPartyQuestionnaire
// for a consistent "modal == intentional act" UX feel.
//
// Submits { fabric_b64, fabric_preview_url, category, style, garment_name }
// to the parent which fires the generation off via the existing
// `runCustomFabricGenerate` pipeline.
//
// Feb 2026 additions:
//   • Auto-cleanup on upload — the swatch is quietly run through the
//     same AI cleaner used on the /fabrics admin page (background
//     removal, top-down flat, even lighting). Raw preview shows
//     instantly, cleaned version swaps in a few seconds later.
//   • Master-admin only "Save to Library & Assign to Stores" section —
//     turns a one-off upload into a permanent library fabric usable across
//     multiple stores in one action.
// ────────────────────────────────────────────────────────────────────────
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Loader2, Sparkles, Upload, X, Store, Footprints, Shirt as ShirtIcon } from 'lucide-react';
import { toast } from 'sonner';
// Iter 108 — reuse the CustomizeModal's exact pant palette so a Sport
// Coat render from a one-off upload dictates the SAME 12 preset trouser
// options a tailor sees in the fabric-tab pant editor.
import { PANT_COLORS } from './CustomizeModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MASTER_EMAIL = 'robson@tailorflow.tech';

// Mirrors the silhouette tree in the Custom Fabric dropdown buttons so the
// modal can offer the same coverage when uploading a one-off swatch.
const SILHOUETTE_TREE = [
  {
    category_key: 'full_suits', category_label: 'Suits',
    silhouettes: [
      { id: 'classic_suit',   label: 'Single-Breasted Suit',  garment: 'Single-Breasted Suit' },
      { id: 'double_breasted', label: 'Double-Breasted Suit', garment: 'Double-Breasted Suit' },
      { id: 'three_piece',    label: 'Three-Piece Suit',      garment: 'Three-Piece Suit' },
      { id: 'tuxedo',         label: 'Tuxedo',                garment: 'Tuxedo' },
    ],
  },
  {
    category_key: 'business_casual', category_label: 'Sports Coat',
    silhouettes: [
      { id: 'single_blazer', label: 'Single-Breasted Sports Coat', garment: 'Sports Coat' },
      { id: 'double_blazer', label: 'Double-Breasted Sports Coat', garment: 'Double-Breasted Sports Coat' },
    ],
  },
  {
    category_key: 'outerwear', category_label: 'Overcoats',
    silhouettes: [
      { id: 'overcoat',    label: 'Overcoat',     garment: 'Overcoat' },
      { id: 'car_coat',    label: 'Car Coat',     garment: 'Car Coat' },
      { id: 'trench_coat', label: 'Trench Coat',  garment: 'Trench Coat' },
    ],
  },
  {
    category_key: 'shirts', category_label: 'Shirts',
    silhouettes: [
      { id: 'classic_dress_shirt', label: 'Classic Dress Shirt', garment: 'Classic Dress Shirt' },
      { id: 'spread_collar',       label: 'Spread Collar Shirt', garment: 'Spread Collar Shirt' },
      { id: 'oxford_shirt',        label: 'Oxford Button-Down',  garment: 'Oxford Button-Down Shirt' },
      { id: 'polo',                label: 'Polo',                garment: 'Polo Shirt' },
    ],
  },
];

// Shoe / shirt selectors mirror CustomFabricQuestionnaire so an uploaded
// swatch offers the SAME styling coverage as a library-fabric render.
const SHOE_STYLES = [
  { id: 'oxford',       label: 'Oxford' },
  { id: 'derby',        label: 'Derby' },
  { id: 'loafer',       label: 'Loafer' },
  { id: 'monk',         label: 'Monk-Strap' },
  { id: 'chelsea_boot', label: 'Chelsea Boot' },
  { id: 'cap_toe',      label: 'Cap-Toe Oxford' },
  // Feb 2026 — casual white court sneaker option.
  { id: 'sneaker',      label: 'Sneaker' },
];
const SHOE_COLOURS = [
  { id: 'Black',      hex: '#0A0A0C' },
  { id: 'Dark Brown', hex: '#3A2418' },
  { id: 'Chocolate',  hex: '#503020' },
  { id: 'Cognac',     hex: '#9B5E2E' },
  { id: 'Tan',        hex: '#C19A6B' },
  { id: 'Oxblood',    hex: '#582430' },
  { id: 'White',      hex: '#F5F0E1' },
];
const SHIRT_STYLES = [
  { id: 'spread_collar',  label: 'Spread Collar' },
  { id: 'point_collar',   label: 'Point Collar' },
  { id: 'cutaway_collar', label: 'Cutaway Collar' },
  { id: 'button_down',    label: 'Button-Down' },
  { id: 'club_collar',    label: 'Club Collar' },
  { id: 'band_collar',    label: 'Band / No Collar' },
];
const SHIRT_COLOURS = [
  { id: 'White',         hex: '#FFFFFF' },
  { id: 'Light Blue',    hex: '#BFD9F2' },
  { id: 'Cream',         hex: '#F5F0E1' },
  { id: 'Light Pink',    hex: '#F5D7D9' },
  { id: 'Pale Lavender', hex: '#DCD0EC' },
  { id: 'Light Grey',    hex: '#D9D9D9' },
  { id: 'Pale Mint',     hex: '#CDE3D2' },
  { id: 'Black',         hex: '#1A1A1A' },
];

// Feb 2026 — lapel + pocket options for jacket-bearing categories
// (Suits, Sports Coats, Overcoats). Mirror the same catalogue exposed
// by CustomFabricQuestionnaire so an uploaded swatch renders with the
// same jacket-construction coverage as a library-fabric render.
const LAPEL_CHOICES = [
  { id: 'notch', label: 'Notch',  hint: 'Everyday · business' },
  { id: 'peak',  label: 'Peak',   hint: 'Formal · double-breasted' },
  { id: 'shawl', label: 'Shawl',  hint: 'Tuxedo · rounded' },
];
const POCKET_CHOICES = [
  { id: '',        label: 'No Preference' },
  { id: 'flapped', label: 'Flapped' },
  { id: 'jetted',  label: 'Jetted' },
  { id: 'patched', label: 'Patched' },
];

// Base64 helpers for converting between the browser File API and the
// backend's b64 payloads. `dataURLToFile` lets us re-upload a cleaned
// swatch b64 as a multipart image for the /admin/fabrics endpoint.
function b64ToFile(b64, filename = 'swatch.jpg', mime = 'image/jpeg') {
  const bytes = atob(b64);
  const buf = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i);
  return new File([buf], filename, { type: mime });
}

export default function AddCustomSwatchModal({ open, onClose, onSubmit, isGenerating, currentUser, hasClientImage = true }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [category, setCategory] = useState('full_suits');
  const [silhouetteId, setSilhouetteId] = useState('classic_suit');
  // Shoe + shirt state — matches the CustomFabricQuestionnaire so an
  // uploaded swatch renders with the same styling coverage as any
  // library fabric would.
  const [shoeStyle, setShoeStyle]     = useState('oxford');
  const [shoeColour, setShoeColour]   = useState('Black');
  const [shirtStyle, setShirtStyle]   = useState('spread_collar');
  const [shirtColour, setShirtColour] = useState('White');
  // Iter 108 — Sport Coat category dictates its own trouser colour
  // (the fabric is the jacket only, not the pants), so we expose the
  // same pant palette CustomizeModal uses. Default charcoal.
  const [pantHex, setPantHex]         = useState('#36454F');
  const [pantName, setPantName]       = useState('Charcoal');
  // Feb 2026 — jacket lapel + pocket picks. Only propagated for
  // jacket-bearing categories (full_suits / business_casual /
  // outerwear) — shirt-only uploads ignore these.
  const [lapel,       setLapel]       = useState('notch');
  const [pocketStyle, setPocketStyle] = useState('');
  // Iter 113 — No-tie opt-in. When true, the render pipeline sends
  // `tie_type='none'` which triggers the backend's FINAL NECKWEAR
  // AUTHORITY clause suppressing every mandatory-tie rule (SUIT_TIE_RULE,
  // wedding formality, etc.). Off by default — matches the historical
  // behaviour where the render decides based on category.
  const [noTie, setNoTie] = useState(false);
  // Auto-analysis in background (same flow as /fabrics admin page). The
  // raw upload preview is shown INSTANTLY so the tailor sees their photo,
  // then swaps to the cleaned swatch as soon as the AI pipeline returns.
  // Metadata (pattern_type, primary_color_hex, etc.) is captured so the
  // downstream render can apply the pattern-proportion math correctly.
  const [cleaning, setCleaning] = useState(false);
  const [wasCleaned, setWasCleaned] = useState(false);
  // Fabric analysis metadata returned by /api/fabrics/analyze — mirrors
  // the payload the /fabrics admin page gets, so the render pipeline
  // can honour pattern proportion rules AND lean on the sampled
  // primary colour when Gemini's own reading is off.
  const [analysis, setAnalysis] = useState(null);
  // Cleanup outcome tracks whether the AI actually produced a cleaned
  // image. `null` = not yet run, `true` = clean succeeded, `false` =
  // Gemini refused / returned no image. The Render button surfaces a
  // warning in the `false` case so the tailor knows before burning a
  // credit on a noisy raw upload.
  const [cleanupOk, setCleanupOk] = useState(null);
  // { pattern_type, color_family, primary_color_hex, suggested_name, weave }

  // Master-admin only — save-to-library + multi-store assignment state.
  const isMasterAdmin = ((currentUser?.email) || '').toLowerCase().trim() === MASTER_EMAIL;
  const [stores, setStores] = useState([]);
  const [selectedStoreIds, setSelectedStoreIds] = useState(new Set());
  const [saveToLibrary, setSaveToLibrary] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveSku, setSaveSku] = useState('');
  const [savingFabric, setSavingFabric] = useState(false);

  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      // Hard reset on close so re-opening never carries stale state
      setFile(null);
      setPreview(null);
      setCategory('full_suits');
      setSilhouetteId('classic_suit');
      setShoeStyle('oxford');
      setShoeColour('Black');
      setShirtStyle('spread_collar');
      setShirtColour('White');
      setPantHex('#36454F');
      setPantName('Charcoal');
      setLapel('notch');
      setPocketStyle('');
      setNoTie(false);
      setWasCleaned(false);
      setCleaning(false);
      setAnalysis(null);
      setCleanupOk(null);
      setSelectedStoreIds(new Set());
      setSaveToLibrary(false);
      setSaveName('');
      setSaveSku('');
    }
  }, [open]);

  // Fetch the store list once when the modal opens for the master admin.
  useEffect(() => {
    if (!open || !isMasterAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/admin/stores`, { withCredentials: true });
        if (!cancelled) setStores(Array.isArray(data) ? data : (data?.stores || []));
      } catch (e) {
        // Silent — admin may not have any stores yet, or the endpoint
        // is temporarily unreachable. The "save to library" section
        // simply won't populate.
      }
    })();
    return () => { cancelled = true; };
  }, [open, isMasterAdmin]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Backend accepts up to 8 MB — mirror that so phone photos don't
    // silently reject. Previously 4 MB caused legit tailor uploads
    // (~5-7 MB from an iPhone) to fail with a toast that many missed.
    if (f.size > 8 * 1024 * 1024) {
      toast.error('Swatch image is over 8 MB — please pick a smaller one');
      return;
    }
    const r = new FileReader();
    r.onload = (ev) => {
      const b64 = ev.target.result.split(',')[1];
      // Show the raw preview immediately so the upload feels instant.
      setFile({ raw: f, b64, dataUrl: ev.target.result });
      setPreview(ev.target.result);
      setWasCleaned(false);
      setAnalysis(null);
      setCleanupOk(null);
      // Kick off background analysis — same pipeline the /fabrics admin
      // page uses. Silently falls back to the raw upload if it fails so
      // the tailor can always still hit Render.
      autoAnalyseSwatch(f);
    };
    // Clear the input value so re-selecting the same file re-fires
    // onChange (browsers dedupe identical file selections otherwise).
    r.readAsDataURL(f);
    e.target.value = '';
  };

  // Fire the same AI analyser used on the /fabrics admin page against
  // the newly-uploaded swatch. Returns cleaned image + pattern/colour/
  // weave metadata so the render can honour pattern-proportion math.
  const autoAnalyseSwatch = async (rawFile) => {
    setCleaning(true);
    try {
      const fd = new FormData();
      fd.append('image', rawFile);
      const { data } = await axios.post(
        `${BACKEND_URL}/api/fabrics/analyze`, fd,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 90000,
        },
      );
      // Capture metadata regardless of whether cleanup image came back
      // — the pattern_type + primary_hex still improve the render even
      // when the swatch image itself couldn't be re-rendered.
      // Feb 2026 — capture the enriched analyser fields (composition,
      // sheen, weight, season, hand_feel) so the "save to library" flow
      // persists them AND the render prompt picks them up instead of
      // defaulting every fabric to "Wool & Silk".
      setAnalysis({
        pattern_type: data?.pattern_type || 'solid',
        color_family: data?.color_family || null,
        primary_color_hex: data?.primary_color_hex || null,
        suggested_name: data?.suggested_name || null,
        weave: data?.weave || null,
        composition: data?.composition || null,
        sheen: data?.sheen || null,
        weight: data?.weight || null,
        season: data?.season || null,
        hand_feel: data?.hand_feel || null,
        sku: data?.sku || null,
      });
      const cleanedB64 = data?.cleaned_image_b64;
      if (cleanedB64) {
        const dataUrl = `data:image/jpeg;base64,${cleanedB64}`;
        setFile({ raw: null, b64: cleanedB64, dataUrl });
        setPreview(dataUrl);
        setWasCleaned(true);
        setCleanupOk(true);
      } else {
        // Gemini returned metadata but no cleaned image — flag it so the
        // Render button warns the tailor before firing a paid render on
        // the raw upload (which is what burns credits on bad results).
        setCleanupOk(false);
      }
    } catch (e) {
      console.warn('Swatch auto-analysis failed:', e);
      setCleanupOk(false);
    }
    setCleaning(false);
  };

  const toggleStore = (id) => {
    setSelectedStoreIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Persist the current swatch as a permanent library fabric AND assign
  // it to the selected stores in one action. Master-admin only.
  const handleSaveToLibrary = async () => {
    if (!file) { toast.error('Upload a swatch first'); return; }
    const name = saveName.trim();
    const sku = saveSku.trim().toUpperCase();
    if (!name) { toast.error('Give the fabric a name'); return; }
    if (!sku)  { toast.error('Give the fabric a SKU'); return; }
    setSavingFabric(true);
    try {
      const fd = new FormData();
      fd.append('image', b64ToFile(file.b64, `${sku}.jpg`, 'image/jpeg'));
      fd.append('sku', sku);
      fd.append('name', name);
      // Feb 2026 — forward the enriched analyser output so library
      // fabrics render with the correct composition / sheen / weave
      // instead of the old "Wool & Silk" default. Every field is
      // optional server-side, so blanks are fine.
      if (analysis?.composition) fd.append('composition', analysis.composition);
      if (analysis?.weave) fd.append('weave', analysis.weave);
      if (analysis?.color_family) fd.append('color_family', analysis.color_family);
      if (analysis?.primary_color_hex) fd.append('primary_color_hex', analysis.primary_color_hex);
      if (analysis?.sheen) fd.append('sheen', analysis.sheen);
      if (analysis?.weight) fd.append('weight', analysis.weight);
      if (analysis?.season) fd.append('season', analysis.season);
      if (analysis?.hand_feel) fd.append('hand_feel', analysis.hand_feel);
      if (analysis?.pattern_type) fd.append('pattern_type', analysis.pattern_type);
      // Feb 2026 — corrected endpoint. Was posting to `/api/admin/fabrics`
      // which returns 404 (the actual handler is `.../create`), so
      // save-to-library was silently failing. Now uses the real route.
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/fabrics/create`, fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const newFabricId = data?.fabric?.id || data?.id;
      // If any stores are selected, assign them in a second call.
      if (newFabricId && selectedStoreIds.size > 0) {
        await axios.patch(
          `${BACKEND_URL}/api/admin/fabrics/${newFabricId}/assign`,
          { store_ids: Array.from(selectedStoreIds) },
          { withCredentials: true },
        );
      }
      toast.success(selectedStoreIds.size > 0
        ? `Saved to library · assigned to ${selectedStoreIds.size} store${selectedStoreIds.size === 1 ? '' : 's'}`
        : 'Saved to library');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save to library');
    }
    setSavingFabric(false);
  };

  const handleSubmit = () => {
    if (!file) { toast.error('Upload a swatch first'); return; }
    const cat = SILHOUETTE_TREE.find(c => c.category_key === category);
    const sil = cat?.silhouettes.find(s => s.id === silhouetteId);
    if (!sil) { toast.error('Pick a silhouette'); return; }
    const isShirtCategory = category === 'shirts';
    onSubmit({
      fabric_b64: file.b64,
      fabric_preview_url: file.dataUrl,
      category: cat.category_key,
      style: sil.id,
      garment_name: sil.garment,
      // Shoe is irrelevant for shirt-only renders — send null so the
      // backend prompt skips the shoe clause altogether.
      shoe: isShirtCategory ? null : shoeStyle,
      shoeColour: isShirtCategory ? null : shoeColour,
      shirtStyle,
      shirtColour,
      pantHex: category === 'business_casual' ? pantHex : null,
      pantName: category === 'business_casual' ? pantName : null,
      noTie,
      // Feb 2026 — lapel + pocket (jacket-bearing categories only).
      lapel:       isShirtCategory ? null : lapel,
      pocketStyle: isShirtCategory ? null : (pocketStyle || null),
      // Analysis metadata — lets the render pipeline pipe pattern_type
      // into `suit_pattern` and use the sampled primary hex when
      // building the combo, matching the library-fabric flow.
      pattern_type: analysis?.pattern_type || 'solid',
      primary_color_hex: analysis?.primary_color_hex || null,
      color_family: analysis?.color_family || null,
      fabric_name: analysis?.suggested_name || null,
      weave: analysis?.weave || null,
    });
  };

  const activeCategory = SILHOUETTE_TREE.find(c => c.category_key === category);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !isGenerating && onClose?.()}>
      <DialogContent
        className="max-w-3xl bg-[#0A0A0C] border border-[#27272A] rounded-none p-0 max-h-[92vh] overflow-y-auto"
        data-testid="add-custom-swatch-modal"
      >
        <DialogHeader className="px-6 py-5 border-b border-[#27272A] sticky top-0 bg-[#0A0A0C] z-10">
          <DialogTitle className="text-2xl font-light text-[#F5F5F5]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Add Custom Swatch
          </DialogTitle>
          <DialogDescription className="text-xs text-[#A1A1AA] mt-1">
            Upload a one-off fabric photo, pick the silhouette, and render — the swatch becomes the hero of this look.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-6">
          {/* Swatch upload */}
          <section>
            <Label className="label-luxury mb-2 block">Fabric Swatch</Label>
            <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-4">
              <label
                htmlFor="custom-swatch-file-input"
                className="block aspect-square bg-[#121215] border border-dashed border-[#3F3F46] hover:border-[hsl(var(--accent))] cursor-pointer overflow-hidden flex items-center justify-center transition-colors relative group"
                data-testid="custom-swatch-picker"
              >
                <input
                  id="custom-swatch-file-input"
                  ref={inputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/heic,image/webp"
                  className="hidden"
                  onChange={handleFile}
                />
                {preview ? (
                  <>
                    <img src={preview} alt="swatch preview" className="w-full h-full object-cover" />
                    {cleaning && (
                      <div className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center gap-1.5">
                        <Loader2 className="w-5 h-5 text-[hsl(var(--accent))] animate-spin" />
                        <span className="text-[9px] uppercase tracking-wider text-[#F5F5F5]">Cleaning swatch…</span>
                      </div>
                    )}
                    {wasCleaned && !cleaning && (
                      <span className="absolute bottom-2 left-2 text-[9px] uppercase tracking-wider px-2 py-0.5 bg-[hsl(var(--accent))]/90 text-[#0A0A0C]">
                        Cleaned
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setFile(null); setPreview(null); setWasCleaned(false); }}
                      className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-red-900/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid="custom-swatch-clear"
                    >
                      <X className="w-3.5 h-3.5 text-white" />
                    </button>
                  </>
                ) : (
                  <div className="text-center px-3">
                    <Upload className="w-7 h-7 mx-auto text-[#52525B] mb-2" />
                    <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">Swatch Image</p>
                    <p className="text-[10px] text-[#52525B] mt-1">PNG or JPEG · 8MB max</p>
                  </div>
                )}
              </label>
              <div className="space-y-2 text-xs text-[#A1A1AA] leading-relaxed flex flex-col justify-center">
                <p>Centre the swatch in the frame, soft daylight or studio light, no shadows.</p>
                <p className="text-[#52525B]">The texture, weave and sheen are what the AI reads — sharp focus on the cloth matters more than aspect ratio.</p>
                {/* Feb 2026 — swatch is auto-cleaned in the background
                    (background removed, top-down flat crop, even lighting)
                    using the same pipeline as the /fabrics admin page. */}
                {cleaning && (
                  <p className="text-[10px] text-[hsl(var(--accent))] uppercase tracking-wider">
                    Auto-cleaning in background…
                  </p>
                )}
                {wasCleaned && !cleaning && (
                  <p className="text-[10px] text-[hsl(var(--accent))] uppercase tracking-wider">
                    ✓ Background + label removed · cropped to cloth
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* Category + Silhouette pickers */}
          <section className="pt-2 border-t border-[#27272A]">
            <Label className="label-luxury mb-2 block">Category</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              {SILHOUETTE_TREE.map(cat => (
                <button
                  key={cat.category_key}
                  type="button"
                  onClick={() => {
                    setCategory(cat.category_key);
                    setSilhouetteId(cat.silhouettes[0].id);
                  }}
                  className={`px-3 py-2.5 text-xs uppercase tracking-wider border transition-all ${
                    category === cat.category_key
                      ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]'
                      : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'
                  }`}
                  data-testid={`custom-swatch-cat-${cat.category_key}`}
                >
                  {cat.category_label}
                </button>
              ))}
            </div>

            <Label className="label-luxury mb-2 block">Silhouette</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(activeCategory?.silhouettes || []).map(sil => (
                <button
                  key={sil.id}
                  type="button"
                  onClick={() => setSilhouetteId(sil.id)}
                  className={`p-3 border text-left transition-all ${
                    silhouetteId === sil.id
                      ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10'
                      : 'border-[#27272A] hover:border-[#52525B]'
                  }`}
                  data-testid={`custom-swatch-sil-${sil.id}`}
                >
                  <p className="text-sm text-[#F5F5F5]">{sil.label}</p>
                </button>
              ))}
            </div>
          </section>

          {/* Iter 109 v5 — Shoe · Shirt · Trouser (Sport-Coat only) picker.
              Visual patterns match CustomFabricQuestionnaire EXACTLY so
              the one-off swatch flow and the assigned-fabric flow read
              as the same popup. Shoe + Shirt side-by-side on desktop,
              trouser sits below when the sport-coat category is active. */}
          <section
            className="pt-2 border-t border-[#27272A] grid grid-cols-1 lg:grid-cols-2 gap-6"
            data-testid="custom-swatch-config-grid"
          >
            {/* Shoe */}
            <div className={category === 'shirts' ? 'opacity-40 pointer-events-none' : ''}>
              <Label className="label-luxury mb-2 flex items-center gap-2">
                <Footprints className="w-3.5 h-3.5" /> Shoe
                {category === 'shirts' && (
                  <span className="text-[10px] text-[#52525B] uppercase normal-case ml-auto">N/A for shirts</span>
                )}
              </Label>
              <div className="space-y-3">
                <div>
                  <span className="block text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1.5">Style</span>
                  <div className="grid grid-cols-2 gap-1.5">
                    {SHOE_STYLES.map(s => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setShoeStyle(s.id)}
                        className={`px-2 py-2 text-xs border transition-all ${
                          shoeStyle === s.id
                            ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]'
                            : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'
                        }`}
                        data-testid={`custom-swatch-shoe-${s.id}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="block text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1.5">Colour</span>
                  <div className="grid grid-cols-4 gap-1.5">
                    {SHOE_COLOURS.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setShoeColour(c.id)}
                        className={`flex flex-col items-center gap-1 p-2 border transition-all ${
                          shoeColour === c.id
                            ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10'
                            : 'border-[#27272A] hover:border-[#52525B]'
                        }`}
                        data-testid={`custom-swatch-shoe-colour-${c.id.replace(/\s+/g, '_')}`}
                      >
                        <div className="w-6 h-6 border border-black/30" style={{ backgroundColor: c.hex }} />
                        <span className="text-[10px] text-[#A1A1AA] leading-tight text-center">{c.id}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Shirt */}
            <div>
              <Label className="label-luxury mb-2 flex items-center gap-2">
                <ShirtIcon className="w-3.5 h-3.5" /> Shirt
              </Label>
              <div className="space-y-3">
                <div>
                  <span className="block text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1.5">Style</span>
                  <div className="grid grid-cols-2 gap-1.5">
                    {SHIRT_STYLES.map(s => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setShirtStyle(s.id)}
                        className={`px-2 py-2 text-xs border transition-all ${
                          shirtStyle === s.id
                            ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]'
                            : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'
                        }`}
                        data-testid={`custom-swatch-shirt-${s.id}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="block text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1.5">Colour</span>
                  <div className="grid grid-cols-4 gap-1.5">
                    {SHIRT_COLOURS.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setShirtColour(c.id)}
                        className={`flex flex-col items-center gap-1 p-2 border transition-all ${
                          shirtColour === c.id
                            ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10'
                            : 'border-[#27272A] hover:border-[#52525B]'
                        }`}
                        data-testid={`custom-swatch-shirt-colour-${c.id.replace(/\s+/g, '_')}`}
                      >
                        <div className="w-6 h-6 border border-black/30" style={{ backgroundColor: c.hex }} />
                        <span className="text-[10px] text-[#A1A1AA] leading-tight text-center">{c.id}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── Feb 2026 — Lapel + Pockets (jacket-bearing only) ─────────
              Surfaces the same lapel / pocket coverage the
              CustomFabricQuestionnaire has, so a swatch uploaded via
              this modal renders with the correct jacket construction. */}
          {category !== 'shirts' && (
            <section
              className="pt-4 border-t border-[#27272A] grid grid-cols-1 lg:grid-cols-2 gap-6"
              data-testid="custom-swatch-jacket-section"
            >
              <div>
                <Label className="label-luxury mb-2 block">
                  Lapel
                  <span className="text-[10px] text-[#52525B] uppercase tracking-wider normal-case ml-2">
                    Jacket collar shape
                  </span>
                </Label>
                <div className="grid grid-cols-3 gap-1.5">
                  {LAPEL_CHOICES.map(l => (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => setLapel(l.id)}
                      className={`p-3 border text-left transition-all ${lapel === l.id
                        ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]'
                        : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'}`}
                      data-testid={`custom-swatch-lapel-${l.id}`}
                    >
                      <p className="text-xs uppercase tracking-wider">{l.label}</p>
                      <p className="text-[9px] text-[#52525B] mt-0.5 normal-case">{l.hint}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="label-luxury mb-2 block">
                  Pockets
                  <span className="text-[10px] text-[#52525B] uppercase tracking-wider normal-case ml-2">
                    Hip pocket construction
                  </span>
                </Label>
                <div className="grid grid-cols-2 gap-1.5">
                  {POCKET_CHOICES.map(p => (
                    <button
                      key={p.id || 'none'}
                      type="button"
                      onClick={() => setPocketStyle(p.id)}
                      className={`px-2 py-2 text-xs border transition-all ${pocketStyle === p.id
                        ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]'
                        : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'}`}
                      data-testid={`custom-swatch-pocket-${p.id || 'none'}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Trouser · Sport-Coat only. Matches CustomFabricQuestionnaire
              trouser section exactly. */}
          {category === 'business_casual' && (
            <section
              className="pt-4 border-t border-[#27272A]"
              data-testid="custom-swatch-pant-section"
            >
              <Label className="label-luxury mb-2 flex items-center gap-2">
                <span>Trousers</span>
                <span className="text-[10px] text-[#52525B] uppercase tracking-wider normal-case ml-2">
                  {pantName} — sport coat pairs with this trouser colour
                </span>
              </Label>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
                {PANT_COLORS.map(c => {
                  const selected = pantHex === c.value;
                  return (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => { setPantHex(c.value); setPantName(c.name); }}
                      title={c.name}
                      className={`flex flex-col items-center gap-1 p-2 border transition-all ${
                        selected
                          ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10'
                          : 'border-[#27272A] hover:border-[#52525B]'
                      }`}
                      data-testid={`custom-swatch-pant-${c.name.replace(/\s+/g, '_')}`}
                    >
                      <div className="w-6 h-6 border border-black/30" style={{ backgroundColor: c.value }} />
                      <span className="text-[10px] text-[#A1A1AA] leading-tight text-center">{c.name}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Iter 113 — No-tie opt-in toggle. Hidden for `shirts` category
              (shirts don't render neckwear anyway) since there's no tie
              to suppress. Shown for full_suits / business_casual / outerwear
              so the tailor can request an open-collar render for any custom
              fabric. Backend sends `tie_type='none'` when checked. */}
          {category !== 'shirts' && (
            <section
              className="pt-4 border-t border-[#27272A]"
              data-testid="custom-swatch-notie-section"
            >
              <button
                type="button"
                onClick={() => setNoTie(v => !v)}
                className={`w-full flex items-center gap-3 p-3 border transition-all text-left ${
                  noTie
                    ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10'
                    : 'border-[#27272A] hover:border-[#52525B]'
                }`}
                data-testid="custom-swatch-notie-toggle"
                aria-pressed={noTie}
              >
                <div className={`w-4 h-4 border ${noTie ? 'bg-[hsl(var(--accent))] border-[hsl(var(--accent))]' : 'border-[#52525B]'} flex items-center justify-center flex-shrink-0`}>
                  {noTie && (
                    <svg className="w-3 h-3 text-[#0A0A0C]" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.5 6.5L5 9L10 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-widest text-[#F5F5F5] font-medium">No Tie</p>
                  <p className="text-[10px] text-[#52525B] uppercase tracking-wider normal-case mt-0.5">
                    Render with an open collar — no necktie, no bow tie, no ascot
                  </p>
                </div>
              </button>
            </section>
          )}

          {/* Auto-detected metadata pill — surfaces what the AI read
              from the swatch (pattern, colour, weave) so the tailor
              knows the render will honour pattern proportion. */}
          {analysis && (
            <section
              className="p-3 bg-[#0F1116] border border-[#27272A] flex flex-wrap gap-2 items-center text-[10px] uppercase tracking-wider"
              data-testid="custom-swatch-detected-meta"
            >
              <span className="text-[#A1A1AA]">AI detected:</span>
              {analysis.pattern_type && analysis.pattern_type !== 'solid' && (
                <span className="px-2 py-0.5 bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] border border-[hsl(var(--accent))]/40">
                  {String(analysis.pattern_type).replace(/_/g, ' ')}
                </span>
              )}
              {analysis.color_family && (
                <span className="flex items-center gap-1.5 px-2 py-0.5 bg-[#1A1A1E] text-[#F5F5F5] border border-[#3F3F46]">
                  {analysis.primary_color_hex && (
                    <span className="w-2.5 h-2.5 border border-[#3F3F46]" style={{ background: analysis.primary_color_hex }} />
                  )}
                  {analysis.color_family}
                </span>
              )}
              {analysis.weave && analysis.weave !== 'tbd' && (
                <span className="px-2 py-0.5 bg-[#1A1A1E] text-[#A1A1AA] border border-[#3F3F46]">
                  {analysis.weave}
                </span>
              )}
            </section>
          )}

          {/* Master-admin only — Save to Library & Assign to Other Stores.
              Turns a one-off upload into a permanent library fabric usable
              across every selected store. Independent from Render — the
              admin can save without rendering, or vice versa. */}
          {isMasterAdmin && (
            <section className="pt-4 border-t border-[#27272A]">
              <label className="flex items-center gap-2 cursor-pointer select-none mb-3"
                     data-testid="custom-swatch-save-toggle">
                <input
                  type="checkbox"
                  checked={saveToLibrary}
                  onChange={(e) => setSaveToLibrary(e.target.checked)}
                  className="accent-[hsl(var(--accent))] w-4 h-4"
                />
                <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#F5F5F5]">
                  <Store className="w-3.5 h-3.5 text-[hsl(var(--accent))]" />
                  Also save to library &amp; add to other stores
                </span>
              </label>

              {saveToLibrary && (
                <div className="pl-6 space-y-3 border-l border-[#27272A]">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1">Fabric Name</label>
                      <input
                        type="text"
                        value={saveName}
                        onChange={(e) => setSaveName(e.target.value)}
                        placeholder="Burgundy Plaid Wool"
                        className="w-full bg-[#0A0A0C] border border-[#27272A] focus:border-[hsl(var(--accent))] px-3 py-2 text-sm text-[#F5F5F5] outline-none"
                        data-testid="custom-swatch-save-name"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1">SKU</label>
                      <input
                        type="text"
                        value={saveSku}
                        onChange={(e) => setSaveSku(e.target.value)}
                        placeholder="CUSTOM-001"
                        className="w-full bg-[#0A0A0C] border border-[#27272A] focus:border-[hsl(var(--accent))] px-3 py-2 text-sm text-[#F5F5F5] outline-none uppercase"
                        data-testid="custom-swatch-save-sku"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1">
                      Assign to Stores ({selectedStoreIds.size})
                    </label>
                    <div className="border border-[#27272A] max-h-40 overflow-y-auto divide-y divide-[#27272A]"
                         data-testid="custom-swatch-store-list">
                      {stores.length === 0 ? (
                        <p className="px-3 py-3 text-xs text-[#52525B]">
                          No stores available. Create stores in the master admin panel first.
                        </p>
                      ) : stores.map(s => {
                        const checked = selectedStoreIds.has(s.id);
                        return (
                          <label
                            key={s.id}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-[#1A1A1E] cursor-pointer"
                            data-testid={`custom-swatch-store-${s.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleStore(s.id)}
                              className="accent-[hsl(var(--accent))] w-3.5 h-3.5"
                            />
                            <span className="text-xs text-[#F5F5F5]">
                              {s.store_name || s.subdomain || s.email}
                            </span>
                            {s.email && (s.store_name || s.subdomain) && (
                              <span className="text-[10px] text-[#52525B] ml-auto">{s.email}</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-[#52525B] mt-1">
                      Leave empty to save the fabric to your master library only (no store sees it yet).
                    </p>
                  </div>

                  <Button
                    type="button"
                    onClick={handleSaveToLibrary}
                    disabled={!file || savingFabric || !saveName.trim() || !saveSku.trim()}
                    className="rounded-none uppercase tracking-wider text-[10px] px-4 py-2 h-9 bg-[#1A1A1E] border border-[hsl(var(--accent))] text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/10"
                    data-testid="custom-swatch-save-submit"
                  >
                    {savingFabric
                      ? (<><Loader2 className="w-3.5 h-3.5 spinner mr-2" /> Saving…</>)
                      : (<><Store className="w-3.5 h-3.5 mr-2" /> Save &amp; Assign</>)}
                  </Button>
                </div>
              )}
            </section>
          )}

          {/* Submit — Render is BLOCKED while auto-cleanup is in flight
              so we never send a raw uncleaned photo to a paid render.
              If cleanup failed (Gemini returned no image), we surface
              a Retry Cleanup CTA + a hard warning so the tailor knows
              they're about to burn a credit on a noisy raw upload. */}
          <div className="pt-4 border-t border-[#27272A] flex flex-col gap-3">
            {cleanupOk === false && !cleaning && (
              <div className="p-3 bg-[#2A1A0F] border border-[#4F2F1F] flex flex-wrap items-center gap-2"
                   data-testid="custom-swatch-cleanup-warning">
                <span className="text-[10px] uppercase tracking-wider text-[#D4A87A]">
                  ⚠ Auto-cleanup failed — Render will use your original photo
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Re-fire cleanup off the current file. If we already
                    // stored the b64 (after a partial fail) we round-trip
                    // it back through b64ToFile so /fabrics/analyze always
                    // receives a real multipart image.
                    if (!file) return;
                    const rawFile = file.raw instanceof File
                      ? file.raw
                      : b64ToFile(file.b64, 'swatch.jpg', 'image/jpeg');
                    autoAnalyseSwatch(rawFile);
                  }}
                  className="h-7 px-3 border-[#4F2F1F] text-[#D4A87A] hover:bg-[#3A2418] uppercase tracking-wider text-[9px]"
                  data-testid="custom-swatch-retry-cleanup"
                >
                  Retry cleanup
                </Button>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <p className="text-[10px] text-[#52525B] uppercase tracking-wider">
                {!hasClientImage
                  ? '⚠ Upload a client photo first — the render needs a face reference.'
                  : !file
                  ? 'Upload a swatch above to enable Render.'
                  : cleaning
                    ? 'Cleaning swatch — Render will unlock in a few seconds…'
                    : cleanupOk === false
                      ? 'Cleanup failed — you can Retry or Render with the raw photo.'
                      : 'Ready — click Render to generate.'}
              </p>
              <Button
                onClick={handleSubmit}
                disabled={!file || isGenerating || cleaning || !hasClientImage}
                title={!hasClientImage ? 'Upload a client photo first — the render needs a face reference' : undefined}
                className="rounded-none uppercase tracking-wider text-xs px-6 py-5 bg-[hsl(var(--accent))] text-[#0A0A0C] hover:bg-[hsl(var(--accent))]/90 disabled:opacity-40"
                data-testid="custom-swatch-submit"
              >
                {isGenerating
                  ? (<><Loader2 className="w-4 h-4 spinner mr-2" /> Generating…</>)
                  : cleaning
                    ? (<><Loader2 className="w-4 h-4 spinner mr-2" /> Cleaning…</>)
                    : (<><Sparkles className="w-4 h-4 mr-2" /> Render Swatch</>)}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
